(function (root, factory) {
  const exported = factory();
  if (typeof module === 'object' && module.exports) { module.exports = exported; return; }
  if (!root.document || root.GiorgioCart || false) return;
  const browser = exported.browserAdapter(root);
  const cart = new exported.CartController(browser);
  root.GiorgioCart = Object.freeze({active: true, version: 1, afterAdd: options => cart.afterAdd(options), refresh: options => cart.refresh(options), add: (data, button) => cart.add(data, button), flush: () => cart.flush(), get pending() { return cart.pending; }});
  browser.bind(cart);
})(typeof window === 'undefined' ? globalThis : window, function () {
  const defaultLabels = {
    error: 'We could not complete this cart update. Review your bag before trying again.',
    refreshError: 'Your cart could not be refreshed. Open your bag to review its current contents.',
    addedRefreshError: 'Your item was added, but the cart preview could not refresh. Open your bag to review it.',
    quantityError: 'Enter a valid whole-number quantity for this item.',
    changedError: 'This cart item changed. Review the refreshed bag and try again.',
    partialError: 'Only part of this grouped item could be updated. Review the refreshed quantities before continuing.',
    termsError: 'Please accept the checkout terms before continuing.',
    notesError: 'Your note could not be saved. Please try again before checking out.',
    discountBusy: 'Please wait while your discount is updated.',
    discountError: 'We could not confirm the discount update. Review your bag and try the code again before checking out.',
    discountTimeout: 'The discount update is taking longer than expected. Refresh your bag and review the code before checking out.',
    removeDiscount: 'Remove discount code __CODE__',
    viewCart: 'View your bag', retry: 'Refresh bag', updating: 'Updating your bag…', discountCode: 'Discount code',
  };
  const discountButtons = new WeakSet(), discountInputLabels = new WeakMap();
  const cartAreaSelector = 'cart-drawer#cart-drawer,[data-cart-page]';
  function captureCartFocus(doc, node = doc.activeElement) {
    const area = node?.closest?.(cartAreaSelector);
    if (!area) return null;
    const row = node.closest('[data-item]');
    const snapshot = {original: node, surface: node.closest('cart-drawer') ? 'drawer' : 'page', cancelled: false, rendering: false};
    if (row) {
      return {...snapshot, key: row.dataset.item, identity: row.dataset.gCartRowIdentity, index: [...area.querySelectorAll('[data-item]')].indexOf(row), kind: node.matches?.('.cart__quantity-field') ? 'quantity' : node.matches?.('.cart__item__remove') ? 'remove' : node.matches?.('a[href]') ? 'link' : 'button', name: node.name, linkIndex: [...row.querySelectorAll('a[href]')].indexOf(node)};
    }
    if (node.closest('[data-g-cart-discount-mount]')) {
      return {...snapshot, kind: node.matches?.('input[name="temp-discount"]') ? 'couponInput' : node.matches?.('.docapp-coupon-input--bean-container--bean-remove') ? 'couponRemove' : 'couponApply', code: node.getAttribute?.('data-code')};
    }
    if (node.name && node.matches?.('input,textarea,select')) return {...snapshot, kind: 'field', name: node.name};
    return null;
  }
  function resolveCartFocus(doc, snapshot) {
    const area = doc.querySelector(snapshot.surface === 'drawer' ? '#cart-drawer' : '[data-cart-page]');
    if (!area) return null;
    if (snapshot.original?.isConnected && snapshot.original.closest(cartAreaSelector) === area) return snapshot.original;
    if (snapshot.kind.startsWith('coupon')) {
      const mount = area.querySelector('[data-g-cart-discount-mount]');
      if (snapshot.kind === 'couponRemove') return [...(mount?.querySelectorAll('.docapp-coupon-input--bean-container--bean-remove') || [])].find(node => node.getAttribute('data-code') === snapshot.code) || mount?.querySelector('input[name="temp-discount"]');
      return mount?.querySelector(snapshot.kind === 'couponInput' ? 'input[name="temp-discount"]' : '.docapp-coupon-input--button') || mount?.querySelector('input[name="temp-discount"]');
    }
    if (snapshot.kind === 'field') return [...area.querySelectorAll('input,textarea,select')].find(node => node.name === snapshot.name);
    const rows = [...area.querySelectorAll('[data-item]')];
    let row = rows.find(node => node.dataset.item === snapshot.key);
    if (!row && snapshot.identity) {
      const matching = rows.filter(node => node.dataset.gCartRowIdentity === snapshot.identity);
      if (matching.length === 1) row = matching[0];
    }
    if (!row && snapshot.kind === 'remove') row = rows[Math.min(Math.max(snapshot.index, 0), rows.length - 1)];
    if (row) {
      const control = snapshot.kind === 'quantity' ? row.querySelector('.cart__quantity-field') : snapshot.kind === 'remove' ? row.querySelector('.cart__item__remove') : snapshot.kind === 'link' ? row.querySelectorAll('a[href]')[Math.max(snapshot.linkIndex, 0)] : [...row.querySelectorAll('button')].find(node => node.name === snapshot.name);
      if (control) return control;
    }
    return [...area.querySelectorAll('[data-cart-heading],#GiorgioCartTitle,[data-cart-drawer-close]')].find(node => !node.getClientRects || node.getClientRects().length);
  }
  function waitForDrawerFocus(win, drawer) {
    const inner = drawer?.querySelector('[data-cart-drawer-inner]');
    const style = inner && win.getComputedStyle?.(inner);
    if (!style || style.animationName === 'none') return Promise.resolve();
    const time = value => Number.parseFloat(value) * (String(value).trim().endsWith('ms') ? 1 : 1000) || 0;
    const durations = (style.animationDuration || '0s').split(',').map(time), delays = (style.animationDelay || '0s').split(',').map(time);
    const duration = Math.max(...durations.map((value, index) => value + delays[index % delays.length]), 0);
    if (!duration) return Promise.resolve();
    return new Promise(resolve => {
      let timer;
      const finish = event => {
        if (event && event.target !== inner) return;
        win.clearTimeout(timer);
        inner.removeEventListener('animationend', finish); inner.removeEventListener('animationcancel', finish);
        resolve();
      };
      inner.addEventListener('animationend', finish); inner.addEventListener('animationcancel', finish);
      timer = win.setTimeout(finish, Math.min(duration + 100, 5000));
    });
  }
  function quantity(value, rules = {}) {
    if (!/^\d+$/.test(String(value))) throw new Error(defaultLabels.quantityError);
    const n = Number(value), min = Number(rules.min || 1), step = Number(rules.step || 1);
    const max = rules.max === undefined || rules.max === '' ? Infinity : Number(rules.max);
    if (!Number.isSafeInteger(n) || n < 0 || (n !== 0 && (!Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(step) || step < 1 || (max !== Infinity && (!Number.isSafeInteger(max) || max < min)) || n < min || n > max || (n - min) % step !== 0))) throw new Error(defaultLabels.quantityError);
    return n;
  }
  function stable(value) {
    if (Array.isArray(value)) return '[' + value.map(stable).join(',') + ']';
    if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + stable(value[key])).join(',') + '}';
    return JSON.stringify(value);
  }
  function identity(line) {
    return stable({variant: String(line.variant_id ?? line.id), properties: line.properties || {}, sellingPlan: line.selling_plan_allocation?.selling_plan?.id || null, parent: line.parent_relationship?.parent_key || null});
  }
  function currentLine(cart, original) {
    const exact = cart.items.find(line => line.key === original.key);
    if (exact) return exact;
    const candidates = cart.items.filter(line => identity(line) === identity(original));
    if (candidates.length !== 1) throw new Error(defaultLabels.changedError);
    return candidates[0];
  }
  function bindDiscountAccessibility(win, mount, labels = {}, hooks = {}) {
    const doc = win.document;
    let focused, revision = 0, focusQueued = false, connected = true;
    const forgetFocus = () => {focused = null; revision++;};
    const rememberFocus = node => {
      if (!node || !mount.contains?.(node)) return;
      const kind = node.matches?.('input[name="temp-discount"]') ? 'input' : node.matches?.('.docapp-coupon-input--button') ? 'apply' : node.matches?.('.docapp-coupon-input--bean-container--bean-remove') ? 'remove' : null;
      if (!kind) return;
      focused = {original: node, kind, code: node.getAttribute('data-code')}; revision++;
    };
    const focusIn = event => {
      if (mount.contains?.(event.target)) rememberFocus(event.target);
      else if (event.target !== doc?.body || focused?.original.isConnected !== false) forgetFocus();
    };
    const pointerDown = event => {if (focused && event.target !== focused.original && !focused.original.contains?.(event.target)) forgetFocus();};
    const keyDown = event => {if (focused && (event.key === 'Tab' || event.key === 'Escape' || event.target !== focused.original)) forgetFocus();};
    function restoreProviderFocus() {
      if (!doc || !focused || focusQueued || focused.original.isConnected !== false || doc.activeElement !== doc.body || hooks.canRestoreFocus?.() === false) return;
      const captured = focused, observed = revision;
      focusQueued = true;
      const frame = callback => win.requestAnimationFrame ? win.requestAnimationFrame(callback) : callback();
      Promise.resolve().then(() => frame(() => frame(() => {
        focusQueued = false;
        if (!connected || revision !== observed || mount.isConnected === false || doc.activeElement !== doc.body || hooks.canRestoreFocus?.() === false) return;
        const input = mount.querySelector('input[name="temp-discount"]');
        const next = captured.kind === 'input' ? input : captured.kind === 'apply' ? mount.querySelector('.docapp-coupon-input--button') || input : [...mount.querySelectorAll('.docapp-coupon-input--bean-container--bean-remove')].find(node => node.getAttribute('data-code') === captured.code) || input;
        if (next && !next.disabled && next.getAttribute('aria-disabled') !== 'true') {next.focus({preventScroll: true}); rememberFocus(next);}
      })));
    }
    function repair() {
      mount.querySelectorAll('input[name="temp-discount"]').forEach(input => {
        const existing = input.getAttribute('aria-label');
        if (existing?.trim() && existing !== discountInputLabels.get(input) || input.getAttribute('aria-labelledby')?.trim() || input.labels?.length) return;
        const label = input.getAttribute('placeholder')?.trim() || labels.discountCode || defaultLabels.discountCode;
        if (existing !== label) input.setAttribute('aria-label', label);
        discountInputLabels.set(input, label);
      });
      mount.querySelectorAll('.docapp-coupon-input--button,.docapp-coupon-input--bean-container--bean-remove').forEach(button => {
        const removing = button.matches('.docapp-coupon-input--bean-container--bean-remove');
        if (removing && !button.getAttribute('aria-label')?.trim()) {
          let code = button.getAttribute('data-code') || '';
          try {code = decodeURIComponent(code);} catch (_) {}
          button.setAttribute('aria-label', (labels.removeDiscount || defaultLabels.removeDiscount).replace('__CODE__', code).trim());
        }
        if (button.matches('button,input[type="button"],input[type="submit"],input[type="reset"]')) return;
        if (button.getAttribute('role') !== 'button') button.setAttribute('role', 'button');
        if (!button.hasAttribute('tabindex')) button.setAttribute('tabindex', '0');
        if (discountButtons.has(button)) return;
        discountButtons.add(button);
        let spaceArmed = false;
        const space = event => event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space';
        button.addEventListener('keydown', event => {
          if (removing && event.key === 'Enter') {
            event.preventDefault();
            if (!event.repeat && !button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
            return;
          }
          if (!space(event)) return;
          event.preventDefault();
          if (!event.repeat) spaceArmed = true;
        });
        button.addEventListener('keyup', event => {
          if (!space(event)) return;
          event.preventDefault();
          const activate = spaceArmed; spaceArmed = false;
          if (activate && !button.disabled && button.getAttribute('aria-disabled') !== 'true' && button.isConnected !== false) button.click();
        });
        button.addEventListener('blur', () => {spaceArmed = false;});
      });
      restoreProviderFocus();
    }
    doc?.addEventListener?.('focusin', focusIn, true);
    doc?.addEventListener?.('giorgio:cart-focus-settled', restoreProviderFocus);
    win.addEventListener?.('pointerdown', pointerDown, true);
    win.addEventListener?.('keydown', keyDown, true);
    rememberFocus(doc?.activeElement);
    repair();
    const observer = typeof win.MutationObserver === 'function' ? new win.MutationObserver(repair) : null;
    observer?.observe(mount, {childList: true, subtree: true, attributes: true, attributeFilter: ['placeholder']});
    return {disconnect: () => {connected = false; observer?.disconnect(); doc?.removeEventListener?.('focusin', focusIn, true); doc?.removeEventListener?.('giorgio:cart-focus-settled', restoreProviderFocus); win.removeEventListener?.('pointerdown', pointerDown, true); win.removeEventListener?.('keydown', keyDown, true);}};
  }
  function nativeDiscountCodes(cart) {
    const codes = new Set();
    for (const row of cart.discount_codes || []) if (row.applicable !== false && row.code) codes.add(String(row.code).trim().toUpperCase());
    const rows = [...(cart.cart_level_discount_applications || []), ...(cart.discounts || []), ...(cart.items || []).flatMap(item => [...(item.line_level_discount_allocations || []), ...(item.discounts || [])])];
    for (const row of rows) {
      const application = row.discount_application || row;
      if (application.type === 'discount_code' && application.title) codes.add(String(application.title).trim().toUpperCase());
    }
    return [...codes].sort();
  }
  class DiscountCoordinator {
    constructor(env) {this.env = env; this.labels = {...defaultLabels, ...env.labels}; this.active = null; this.revision = 0; this.refreshJob = null;}
    get pending() {return Boolean(this.active);}
    begin(action, code) {
      if (this.active) return false;
      let resolve, reject;
      const promise = new Promise((yes, no) => {resolve = yes; reject = no;});
      promise.catch(() => {});
      const operation = {action, code: String(code || '').trim().toUpperCase(), promise, resolve, reject, settling: false};
      this.active = operation;
      operation.timer = this.env.setTimeout(() => this.fail(this.labels.discountTimeout), this.env.timeoutMs || 20000);
      this.env.state?.();
      return true;
    }
    wait() {return this.active?.promise || Promise.resolve();}
    refresh() {
      this.revision++;
      if (this.refreshJob) return this.refreshJob;
      const job = (async () => {
        let observed, result;
        do {
          observed = this.revision;
          result = await this.env.reconcile();
          if (!result?.ok) throw new Error(this.labels.discountError);
        } while (observed !== this.revision);
        return result;
      })();
      this.refreshJob = job.finally(() => {this.refreshJob = null;});
      return this.refreshJob;
    }
    async settle(operation = this.active) {
      if (!operation || operation !== this.active || operation.settling) return;
      operation.settling = true;
      try {
        const result = await this.refresh();
        if (this.active !== operation) return;
        this.env.clearTimeout(operation.timer);
        this.active = null;
        this.env.state?.();
        this.env.settled?.(result.cart);
        operation.resolve(result);
      } catch (_) {this.fail(this.labels.discountError, operation);}
    }
    commit(code) {
      const normalized = String(code || '').trim().toUpperCase();
      if (this.active && (this.active.action === 'apply' ? normalized === this.active.code : normalized !== this.active.code)) {
        if (!this.active.settling) return this.settle();
      }
      this.refresh().catch(() => this.env.notice?.(this.labels.discountError));
    }
    summary(event) {
      const operation = this.active;
      if (!operation || operation.settling || !Array.isArray(event.discounts)) return;
      const expected = event.discounts.map(row => String(row.title || '').trim().toUpperCase()).filter(Boolean);
      if (event.hasFreeShippingDiscount && event.freeShippingDiscount?.title) expected.push(String(event.freeShippingDiscount.title).trim().toUpperCase());
      const codes = [...new Set(expected)].sort();
      if (operation.action === 'apply' && (!operation.code || !codes.includes(operation.code))) return;
      if (operation.action === 'remove' && codes.includes(operation.code)) return;
      this.env.probe().then(cart => {
        if (this.active === operation && !operation.settling && stable(nativeDiscountCodes(cart)) === stable(codes)) this.settle(operation);
      }).catch(() => {});
    }
    fail(message = this.labels.discountError, operation = this.active) {
      if (!operation || operation !== this.active) return;
      this.env.clearTimeout(operation.timer);
      this.active = null;
      this.env.state?.();
      const error = new Error(message); error.giorgioDiscount = true;
      operation.reject(error);
      this.env.notice?.(message);
      this.refresh().catch(() => {}).finally(() => {if (!this.active) this.env.notice?.(message);});
    }
  }
  class CartController {
    constructor(env) {
      this.env = env;
      this.labels = {...defaultLabels, ...env.labels};
      this.tail = Promise.resolve(); this.pending = 0; this.healthy = true; this.lastCart = null;
      this.buttonJobs = new WeakMap(); this.checkoutWaiting = false;
    }
    enqueue(task) {
      this.env.captureFocus?.();
      this.pending++;
      this.env.busy?.(true, this.healthy);
      const result = this.tail.then(task).finally(() => {
        this.pending--;
        this.env.busy?.(this.pending > 0, this.healthy);
      });
      this.tail = result.catch(() => {});
      return result;
    }
    async idle() {
      let observed;
      do { observed = this.tail; await observed; } while (observed !== this.tail);
    }
    async render(cart, options = {}) {
      await this.env.render(cart, options);
      this.lastCart = cart; this.healthy = true;
      this.env.clearNotice?.();
      this.env.changed?.(cart, options);
      this.env.busy?.(this.pending > 0, true);
      return {ok: true, cart, rendered: true};
    }
    async reconcile(options = {}) {
      try { return await this.render(await this.env.read(), options); }
      catch (error) {
        this.healthy = false;
        this.env.busy?.(this.pending > 0, false);
        this.env.notice?.(options.afterAdd ? this.labels.addedRefreshError : this.labels.refreshError, {retry: true});
        return {ok: false, rendered: false, error};
      }
    }
    refresh(options = {}) { return this.enqueue(() => this.reconcile(options)); }
    afterAdd(options = {}) { return this.refresh({...options, open: true, afterAdd: true}); }
    async saveNotes() {
      while (this.env.notesDirty?.()) {
        const notes = this.env.notePayload();
        try {
          const cart = await this.env.request('update.js', notes.payload);
          this.env.notesSaved(notes, cart);
          this.lastCart = cart;
        } catch (error) {
          this.env.notice?.(this.labels.notesError, {retry: true});
          throw error;
        }
      }
    }
    flush() { return this.enqueue(() => this.saveNotes()); }
    add(data, button) {
      if (button && this.buttonJobs.has(button)) return this.buttonJobs.get(button);
      this.env.captureFocus?.();
      this.env.button?.(button, true);
      const job = this.enqueue(async () => {
        try {
          await this.saveNotes();
          const added = await this.env.request('add.js', data);
          const result = await this.reconcile({open: true, opener: button, afterAdd: true});
          return {...result, added: true, item: added};
        } catch (error) {
          const result = await this.reconcile({open: true, opener: button});
          this.env.notice?.(error.message || this.labels.error, {retry: !result.ok});
          return {ok: false, added: false, rendered: result.ok, uncertain: true, error};
        }
      }).finally(() => {
        this.env.button?.(button, false);
        if (button) this.buttonJobs.delete(button);
      });
      if (button) this.buttonJobs.set(button, job);
      return job;
    }
    change(keys, value, rules = {}, options = {}) {
      let wanted;
      try { wanted = quantity(value, rules); }
      catch (error) { this.env.markQuantityInvalid?.(options.input); this.env.notice?.(this.labels.quantityError); return Promise.resolve({ok: false, error}); }
      const requested = [...new Set(keys.filter(key => typeof key === 'string' && key))];
      if (!requested.length) return Promise.resolve({ok: false});
      return this.enqueue(async () => {
        let changed = 0;
        try {
          await this.saveNotes();
          let cart = await this.env.read();
          const originals = requested.map(key => {
            const line = cart.items.find(item => item.key === key);
            if (!line) throw new Error(this.labels.changedError);
            return line;
          });
          for (const original of originals) {
            const line = currentLine(cart, original);
            if (line.quantity === wanted) continue;
            cart = await this.env.request('change.js', {id: line.key, quantity: wanted});
            changed++;
          }
          return await this.render(cart, options);
        } catch (error) {
          const reconciled = await this.reconcile(options);
          const partial = changed > 0 && changed < requested.length;
          this.env.notice?.(partial ? this.labels.partialError : error.message || this.labels.error, {retry: !reconciled.ok});
          return {ok: false, partial, rendered: reconciled.ok, error};
        }
      });
    }
    async checkout(surface) {
      if (this.checkoutWaiting) return;
      this.checkoutWaiting = true;
      try {
        const discount = (this.env.waitDiscount?.() || Promise.resolve()).then(value => ({value}), error => ({error}));
        await this.flush();
        await this.idle();
        const settled = await discount;
        if (settled.error) throw settled.error;
        if (!this.healthy) await this.refresh();
        await this.idle();
        if (!this.healthy) return;
        this.env.checkout?.(surface);
      } catch (error) { this.env.notice?.(error.giorgioDiscount ? error.message : this.labels.notesError, {retry: true}); }
      finally { this.checkoutWaiting = false; }
    }
  }
  function browserAdapter(win) {
    const doc = win.document;
    const areaSelector = cartAreaSelector;
    const drafts = new Map(), locks = new WeakMap(), addingButtons = new WeakMap(), discountObservers = new Map();
    let noteTimer, controller, resumeForm, resumeButton, blockedPress, discounts, focusMemory;
    let config = {};
    try { config = JSON.parse(doc.querySelector('[data-g-cart-config]')?.textContent || '{}'); } catch (_) {}
    const labels = {...defaultLabels, ...config};
    const body = doc.body;
    body.dataset.gCartOwner = 'true';
    const areas = () => [...doc.querySelectorAll(areaSelector)];
    const fields = () => areas().flatMap(area => [...area.querySelectorAll('textarea[name="note"],textarea[name^="attributes["]')]);
    const cartRoot = () => win.Shopify?.routes?.root || '/';
    const cartURL = () => {
      const url = new URL(cartRoot() + 'cart', win.location.origin);
      if (win.Shopify?.theme?.role && win.Shopify.theme.role !== 'main') { url.searchParams.set('preview_theme_id', String(win.Shopify.theme.id)); url.searchParams.set('pb', '0'); }
      return url.href;
    };
    const fieldKey = field => field.name === 'note' ? 'note' : field.name.match(/^attributes\[(.*)\]$/)?.[1];
    const linkedKeys = item => [...new Set([item.dataset.item, ...[...item.querySelectorAll('.c_addon-added[data-key]')].map(node => node.dataset.key)].filter(Boolean))];
    const currentSurface = node => node?.closest('cart-drawer') ? 'drawer' : 'page';
    function rememberFocus(node = doc.activeElement) {
      if (!focusMemory || focusMemory.cancelled) focusMemory = captureCartFocus(doc, node);
    }
    function markQuantityInvalid(input) {
      if (!input) return;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('data-g-cart-quantity-invalid', '');
    }
    function clearQuantityInvalid(input) {
      if (!input) return;
      const error = input.getAttribute('data-g-cart-error-description');
      const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== error);
      if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' ')); else input.removeAttribute('aria-describedby');
      input.removeAttribute('aria-invalid'); input.removeAttribute('data-g-cart-quantity-invalid'); input.removeAttribute('data-g-cart-error-description');
    }
    function syncDiscountMounts() {
      const mounts = new Set(areas().flatMap(area => [...area.querySelectorAll('[data-g-cart-discount-mount],.docapp-cart-with-coupon-summary')]));
      for (const [mount, binding] of discountObservers) if (!mounts.has(mount)) {binding.disconnect(); discountObservers.delete(mount);}
      for (const mount of mounts) if (!discountObservers.has(mount)) discountObservers.set(mount, bindDiscountAccessibility(win, mount, labels, {canRestoreFocus: () => !focusMemory || focusMemory.cancelled}));
    }
    function remember(field) {
      const key = field.name, prior = drafts.get(key);
      if (!prior || prior.value !== field.value) drafts.set(key, {value: field.value, version: (prior?.version || 0) + 1, savedVersion: prior?.savedVersion || 0});
      fields().forEach(other => { if (other !== field && other.name === key) other.value = field.value; });
    }
    function restoreDrafts() { fields().forEach(field => { const draft = drafts.get(field.name); if (draft) field.value = draft.value; }); }
    function setDisabled(node, disabled) {
      if (disabled) { if (!locks.has(node)) locks.set(node, Boolean(node.disabled)); node.disabled = true; }
      else if (locks.has(node)) { node.disabled = locks.get(node); locks.delete(node); }
    }
    function busy(value, healthy) {
      value = value || Boolean(discounts?.pending);
      body.dataset.gCartBusy = String(value);
      body.dataset.gCartDiscountPending = String(Boolean(discounts?.pending));
      areas().forEach(area => {
        area.setAttribute('aria-busy', String(value));
        area.querySelectorAll('.c_cartdrawer-qty,.cart__item__remove,.cart__quantity-field').forEach(node => setDisabled(node, value));
        area.querySelectorAll('[name="checkout"]').forEach(node => setDisabled(node, value || !healthy));
      });
    }
    function notice(text, {retry = false} = {}) {
      const area = doc.querySelector('cart-drawer.is-open') || doc.querySelector('[data-cart-page]');
      let holder = area?.querySelector('[data-g-cart-feedback]') || doc.querySelector('[data-g-cart-global-feedback]');
      if (!holder) {
        holder = doc.createElement('div'); holder.dataset.gCartFeedback = '';
        holder.style.cssText = 'padding:16px;border:1px solid #9d2424;background:#fefbf8;color:#0f0f0f;margin:12px;font:14px/1.5 Inter,Arial,sans-serif;overflow-wrap:anywhere';
        if (!area) {
          holder.dataset.gCartGlobalFeedback = '';
          holder.style.cssText += ';position:fixed;bottom:12px;right:12px;left:12px;width:auto;max-width:440px;max-height:70vh;overflow:auto;z-index:10001;margin:0 0 0 auto;box-shadow:0 8px 40px #0002';
        }
        (area?.querySelector('[data-cart-drawer-body]') || area || body).prepend(holder);
      }
      holder.hidden = false; holder.setAttribute('role', 'alert'); holder.setAttribute('aria-live', 'assertive'); holder.setAttribute('aria-atomic', 'true');
      holder.id ||= area?.matches?.('[data-cart-page]') ? 'GiorgioCartError-page' : area ? 'GiorgioCartError-drawer' : 'GiorgioCartError-global';
      areas().forEach(surface => surface.querySelectorAll('[data-g-cart-quantity-invalid]').forEach(input => {
        if (!surface.contains(holder)) return;
        const ids = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)); ids.add(holder.id);
        input.setAttribute('aria-describedby', [...ids].join(' ')); input.setAttribute('data-g-cart-error-description', holder.id);
      }));
      holder.replaceChildren();
      const p = doc.createElement('p'); p.textContent = text; holder.append(p);
      const link = doc.createElement('a'); link.href = cartURL(); link.textContent = labels.viewCart;
      link.style.cssText = 'display:inline-flex;align-items:center;min-height:48px;margin-right:16px;text-decoration:underline'; holder.append(link);
      if (retry) {
        const button = doc.createElement('button'); button.type = 'button'; button.textContent = labels.retry;
        button.style.cssText = 'min-height:48px;padding:10px;border:1px solid #0f0f0f;background:#fefbf8;color:#0f0f0f';
        button.addEventListener('click', async () => { button.disabled = true; const result = await controller.refresh({open: Boolean(doc.querySelector('cart-drawer.is-open'))}); if (!result.ok && button.isConnected) button.disabled = false; }); holder.append(button);
      }
    }
    async function request(path, payload) {
      const url = new URL(cartRoot() + 'cart/' + path, win.location.origin);
      if (url.origin !== win.location.origin) throw new Error(labels.error);
      const isForm = payload instanceof win.FormData;
      const response = await win.fetch(url.href, {method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json', ...(payload && !isForm ? {'Content-Type': 'application/json'} : {})}, ...(payload ? {body: isForm ? payload : JSON.stringify(payload)} : {})});
      let data; try { data = await response.json(); } catch (_) { throw new Error(labels.error); }
      if (!response.ok || data.status || data.errors) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : typeof data.errors === 'string' ? data.errors : labels.error);
      if (path !== 'add.js' && !Array.isArray(data.items)) throw new Error(labels.error);
      return data;
    }
    function targetSections() {
      const unique = new Map();
      for (const area of areas()) {
        const wrapper = area.closest('.shopify-section[id]');
        const id = wrapper?.id.replace(/^shopify-section-/, '');
        if (!wrapper || !id) throw new Error(labels.refreshError);
        unique.set(id, {id, wrapper, type: currentSurface(area)});
      }
      if (!unique.size || unique.size > 5) throw new Error(labels.refreshError);
      return [...unique.values()];
    }
    async function render(cart, options) {
      const targets = targetSections();
      const url = new URL(win.location.href); url.hash = '';
      url.searchParams.delete('section_id'); url.searchParams.set('sections', targets.map(target => target.id).join(','));
      const response = await win.fetch(url.href, {credentials: 'same-origin', cache: 'no-store'});
      if (!response.ok) throw new Error(labels.refreshError);
      const sections = await response.json();
      const replacements = targets.map(target => {
        if (typeof sections[target.id] !== 'string' || !sections[target.id]) throw new Error(labels.refreshError);
        const parsed = new win.DOMParser().parseFromString(sections[target.id], 'text/html');
        const next = parsed.getElementById(target.wrapper.id);
        if (!next || !next.querySelector(target.type === 'drawer' ? '#cart-drawer' : '[data-cart-page]')) throw new Error(labels.refreshError);
        return {...target, next};
      });
      const priorDrawer = doc.querySelector('#cart-drawer');
      const wasOpen = priorDrawer?.classList.contains('is-open');
      const focus = focusMemory || captureCartFocus(doc);
      if (focus) {focusMemory = focus; focus.rendering = 'replacing';}
      if (wasOpen && typeof priorDrawer.closeCartDrawer === 'function') priorDrawer.closeCartDrawer();
      else if (wasOpen) { priorDrawer.classList.remove('is-open'); doc.dispatchEvent(new win.CustomEvent('theme:cart-drawer:close', {bubbles: true})); }
      for (const replacement of replacements) {
        const existingDiscount = replacement.wrapper.querySelector('.docapp-cart-with-coupon-summary');
        const discountMount = replacement.next.querySelector('[data-g-cart-discount-mount],.docapp-cart-with-coupon-summary');
        if (existingDiscount && discountMount) discountMount.replaceWith(existingDiscount);
        const existingCheckout = replacement.wrapper.querySelector('[data-cart-form] [name="checkout"]');
        const nextCheckout = replacement.next.querySelector('[data-cart-form] [name="checkout"]');
        if (existingCheckout && nextCheckout) nextCheckout.replaceWith(existingCheckout);
        replacement.wrapper.replaceWith(replacement.next);
        replacement.next.dispatchEvent(new win.CustomEvent('shopify:section:load', {bubbles: true, detail: {sectionId: replacement.id}}));
      }
      restoreDrafts();
      if (!options.skipDiscountWidgets && !discounts?.pending && typeof win.discountOnCartProApp?.updateWidgets === 'function') {
        try { win.discountOnCartProApp.updateWidgets(cart); }
        catch (_) { win.console?.warn('MenSuits cart: the existing discount widget could not refresh.'); }
      }
      syncDiscountMounts();
      let drawerFocusReady = Promise.resolve();
      if (options.open || wasOpen) {
        const drawer = doc.querySelector('#cart-drawer');
        if (!drawer) {
          if (!doc.querySelector('[data-cart-page]')) throw new Error(labels.refreshError);
        } else {
          if (typeof drawer.openCartDrawer !== 'function') throw new Error(labels.refreshError);
          if (options.opener?.isConnected && win.accessibility) win.accessibility.lastElement = options.opener;
          drawer.dispatchEvent(new win.CustomEvent('theme:cart-drawer:show', {bubbles: true}));
          if (focus) {drawerFocusReady = waitForDrawerFocus(win, drawer); focus.nativeClose = drawer.querySelector('[data-cart-drawer-close]'); focus.nativeOpener = win.accessibility?.lastElement;}
        }
      }
      if (focus) focus.rendering = 'settling';
      if (focus) Promise.all([drawerFocusReady, Promise.resolve().then(() => controller?.idle?.())]).then(() => win.requestAnimationFrame(() => win.requestAnimationFrame(() => {
        if (!focus.cancelled) {
          const next = resolveCartFocus(doc, focus), dialog = doc.querySelector('dialog[open]');
          if (next && !next.disabled && (!dialog || dialog.contains(next)) && doc.activeElement !== next) {
            if (next.tabIndex < 0 && !next.hasAttribute('tabindex')) next.setAttribute('tabindex', '-1');
            next.focus({preventScroll: true});
          }
        }
        if (focusMemory === focus) {focusMemory = null; doc.dispatchEvent(new win.CustomEvent('giorgio:cart-focus-settled'));}
      })));
    }
    function termsValid(form) {
      const terms = form.querySelector('[data-cart-acceptance-checkbox]');
      if (terms && !terms.checked) { notice(labels.termsError); terms.focus(); return false; }
      return true;
    }
    function stopCheckoutIntent(event) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
    function checkoutIntent(event) {
      const button = event.target.closest?.('[name="checkout"]');
      if (!button?.closest(areaSelector) || resumeButton === button || event.button > 0) return;
      const form = button.closest('[data-cart-form]');
      if (!form) return;
      const press = event.type !== 'click';
      const nextPress = blockedPress?.released && (event.type === 'pointerdown' || event.type === 'touchstart' || event.type === 'mousedown' && blockedPress.kind !== 'touch');
      const keyboardClick = event.type === 'click' && event.detail === 0 && !event.pointerType;
      if (nextPress || keyboardClick && !controller.checkoutWaiting) blockedPress = null;
      if (blockedPress) {
        stopCheckoutIntent(event);
        if (!press) blockedPress = null;
        return;
      }
      const validTerms = termsValid(form);
      if (validTerms && !controller.pending && !env.notesDirty() && controller.healthy && !controller.checkoutWaiting && !discounts?.pending) return;
      stopCheckoutIntent(event);
      if (press) blockedPress = {kind: event.pointerType === 'touch' || event.type === 'touchstart' ? 'touch' : 'mouse', pointerId: event.pointerId, released: false};
      if (!validTerms) return;
      win.clearTimeout(noteTimer);
      controller.checkout(currentSurface(form));
    }
    function releaseCheckoutIntent(event) {
      if (!blockedPress || blockedPress.pointerId !== undefined && event.pointerId !== undefined && blockedPress.pointerId !== event.pointerId) return;
      if (event.type.startsWith('touch') && blockedPress.kind !== 'touch' || event.type === 'mouseup' && blockedPress.kind === 'touch') return;
      blockedPress.released = true;
    }
    function couponIntent(event) {
      const target = event.target;
      const mount = target.closest?.('[data-g-cart-discount-mount],.docapp-cart-with-coupon-summary');
      if (!mount?.closest(areaSelector)) return;
      const removing = target.closest('.docapp-coupon-input--bean-container--bean-remove');
      const applying = target.closest('.docapp-coupon-input--button');
      if (event.type === 'keypress' && event.key !== 'Enter' && event.keyCode !== 13) return;
      if (event.type === 'click' && !removing && !applying || event.type === 'keypress' && !applying && !target.matches('input[name="temp-discount"]')) return;
      let code = removing?.getAttribute('data-code') || mount.querySelector('input[name="temp-discount"]')?.value || '';
      try {if (removing) code = decodeURIComponent(code);} catch (_) {}
      if (!code.trim()) return;
      if (discounts.pending) {event.preventDefault(); event.stopImmediatePropagation(); notice(labels.discountBusy); return;}
      rememberFocus(removing || applying || target);
      discounts.begin(removing ? 'remove' : 'apply', code);
    }
    const env = {
      labels, busy, notice, request, render, captureFocus: rememberFocus, markQuantityInvalid,
      waitDiscount: () => discounts?.wait() || Promise.resolve(),
      clearNotice() { doc.querySelectorAll('[data-g-cart-feedback]').forEach(node => { node.hidden = true; node.replaceChildren(); }); },
      changed(cart, options = {}) {
        if (options.input) clearQuantityInvalid(options.input);
        doc.querySelectorAll('[data-g-cart-count]').forEach(node => { node.textContent = String(cart.item_count); });
        doc.dispatchEvent(new win.CustomEvent('theme:cart:change', {bubbles: true, detail: {cartCount: cart.item_count}}));
      },
      button(button, loading) {
        if (!button) return;
        if (loading) { if (!addingButtons.has(button)) addingButtons.set(button, Boolean(button.disabled)); button.disabled = true; button.classList.add('is-loading'); }
        else if (addingButtons.has(button)) { button.disabled = addingButtons.get(button); addingButtons.delete(button); button.classList.remove('is-loading'); }
      },
      notesDirty: () => [...drafts.values()].some(draft => draft.savedVersion !== draft.version),
      notePayload() {
        const payload = {}, versions = [];
        for (const [name, draft] of drafts) if (draft.savedVersion !== draft.version) {
          const key = name === 'note' ? null : name.match(/^attributes\[(.*)\]$/)?.[1];
          if (name === 'note') payload.note = draft.value;
          else if (key) (payload.attributes ||= {})[key] = draft.value;
          versions.push({name, value: draft.value, version: draft.version});
        }
        return {payload, versions};
      },
      notesSaved(snapshot, cart) {
        for (const row of snapshot.versions) {
          const key = row.name === 'note' ? null : row.name.match(/^attributes\[(.*)\]$/)?.[1];
          const actual = row.name === 'note' ? cart.note || '' : cart.attributes?.[key] || '';
          if (actual !== row.value) throw new Error(labels.notesError);
          const draft = drafts.get(row.name); if (draft?.version === row.version) draft.savedVersion = row.version;
        }
      },
      checkout(surface) {
        const area = doc.querySelector(surface === 'drawer' ? '#cart-drawer' : '[data-cart-page]');
        const form = area?.querySelector('[data-cart-form]'), button = form?.querySelector('[name="checkout"]');
        if (!form || !button || !termsValid(form) || !form.reportValidity()) return;
        resumeForm = form; resumeButton = button;
        try { button.click(); } finally { resumeForm = null; resumeButton = null; }
      },
      bind(instance) {
        controller = instance;
        win.addEventListener('pointerdown', event => {if (focusMemory && event.target !== focusMemory.original && !focusMemory.original?.contains?.(event.target)) focusMemory.cancelled = true;}, true);
        win.addEventListener('keydown', event => {if (focusMemory && (event.key === 'Tab' || event.key === 'Escape' || event.target !== focusMemory.original)) focusMemory.cancelled = true;}, true);
        doc.addEventListener('focusin', event => {if (focusMemory && focusMemory.rendering !== 'replacing' && event.target !== focusMemory.original && event.target !== body && event.target !== focusMemory.nativeClose && event.target !== focusMemory.nativeOpener) focusMemory.cancelled = true;}, true);
        discounts = new DiscountCoordinator({
          labels, setTimeout: (...args) => win.setTimeout(...args), clearTimeout: timer => win.clearTimeout(timer),
          probe: () => controller.enqueue(() => env.read()), reconcile: () => controller.refresh({skipDiscountWidgets: true}),
          state: () => busy(controller.pending > 0, controller.healthy), notice: message => notice(message, {retry: true}),
          settled: cart => {try {win.discountOnCartProApp?.updateWidgets?.(cart);} catch (_) {}},
        });
        syncDiscountMounts();
        doc.addEventListener('shopify:section:load', syncDiscountMounts);
        doc.addEventListener('click', couponIntent, true);
        doc.addEventListener('keypress', couponIntent, true);
        doc.addEventListener('docapp-discount-applying', event => {if (event.isUserAction && !discounts.pending) discounts.begin(event.code ? 'apply' : 'remove', event.code);}, true);
        doc.addEventListener('docapp-discount-code-remove', event => {if (!discounts.pending) discounts.begin('remove', event.code);}, true);
        doc.addEventListener('docapp-discount-applied', event => discounts.summary(event), true);
        doc.addEventListener('docapp-discount-failure', () => discounts.fail(), true);
        doc.addEventListener('docapp-applied-discount-code-changed', event => discounts.commit(event.code), true);
        for (const type of ['pointerdown', 'mousedown', 'touchstart', 'click']) win.addEventListener(type, checkoutIntent, {capture: true, passive: false});
        for (const type of ['pointerup', 'mouseup', 'touchend', 'pointercancel', 'touchcancel']) win.addEventListener(type, releaseCheckoutIntent, {capture: true, passive: true});
        doc.addEventListener('theme:cart:add', event => {
          const button = event.detail?.button, form = button?.closest?.('form');
          if (!form || button.disabled || form.matches('[data-g-cart-external]') || form.closest('giorgio-product')?.dataset.hasAppOptions === 'true' || form.querySelector('input[type="file"],#infiniteoptions-container,[data-g-cart-external],.g-product-app-options [name^="properties["]') || !/\/cart\/add(?:\.js)?$/.test(new URL(form.action, win.location.origin).pathname)) return;
          if (!form.reportValidity()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
          event.preventDefault(); event.stopImmediatePropagation(); controller.add(new win.FormData(form), button);
        }, true);
        doc.addEventListener('click', event => {
          const button = event.target.closest?.('.c_cartdrawer-qty,.cart__item__remove');
          if (!button?.closest(areaSelector)) return;
          event.preventDefault(); event.stopImmediatePropagation();
          if (button.disabled || button.hasAttribute('disabled')) return;
          const item = button.closest('[data-item]'), input = item?.querySelector('.cart__quantity-field');
          if (!item || !input) return;
          const rules = {min: input.min || 1, max: input.max || undefined, step: input.step || 1};
          let wanted;
          try { wanted = button.matches('.cart__item__remove') ? 0 : Math.max(Number(rules.min), quantity(input.value, rules) + (button.name === 'increase' ? 1 : -1) * Number(rules.step)); }
          catch (_) { markQuantityInvalid(input); notice(labels.quantityError); return; }
          input.value = String(wanted);
          controller.change(linkedKeys(item), wanted, rules, {input});
        }, true);
        doc.addEventListener('change', event => {
          const input = event.target;
          if (input.matches?.('.cart__quantity-field') && input.closest(areaSelector)) {
            event.stopImmediatePropagation();
            if (input.disabled) return;
            const item = input.closest('[data-item]');
            if (item) controller.change(linkedKeys(item), input.value, {min: input.min || 1, max: input.max || undefined, step: input.step || 1}, {input});
          }
        }, true);
        const noteInput = event => {
          const field = event.target;
          if (!fields().includes(field) || !fieldKey(field)) return;
          remember(field); win.clearTimeout(noteTimer);
          noteTimer = win.setTimeout(() => { controller.flush().catch(() => {}); }, 400);
        };
        doc.addEventListener('input', noteInput, true);
        doc.addEventListener('change', noteInput, true);
        win.addEventListener('submit', event => {
          const form = event.target, submitter = event.submitter;
          if (!form.matches?.('[data-cart-form]') || !form.closest(areaSelector) || submitter?.name !== 'checkout') return;
          if (resumeForm === form) return;
          if (!termsValid(form)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
          if (!controller.pending && !env.notesDirty() && controller.healthy && !controller.checkoutWaiting && !discounts?.pending) return;
          event.preventDefault(); event.stopImmediatePropagation(); win.clearTimeout(noteTimer);
          controller.checkout(currentSurface(form));
        }, {capture: true});
      },
    };
    env.read = async () => {
      const response = await win.fetch(new URL(cartRoot() + 'cart.js', win.location.origin).href, {credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}});
      const cart = await response.json();
      if (!response.ok || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error(labels.refreshError);
      return cart;
    };
    return env;
  }
  return {CartController, quantity, identity, currentLine, browserAdapter, bindDiscountAccessibility, DiscountCoordinator, nativeDiscountCodes, captureCartFocus, resolveCartFocus, waitForDrawerFocus};
});
